import { Clock, CalendarClock, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface SyncTimestampsCardProps {
  firstSyncedAt: string;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${String(diffMins)} minute${diffMins === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${String(diffHours)} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  if (diffDays < 30) {
    return `${String(diffDays)} day${diffDays === 1 ? '' : 's'} ago`;
  }
  return formatDateTime(isoString);
}

export function SyncTimestampsCard({
  firstSyncedAt,
  lastSyncedAt,
  createdAt,
  updatedAt,
}: SyncTimestampsCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Sync Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* First Synced */}
        <div className="flex items-start gap-3">
          <CalendarClock className="h-4 w-4 text-muted-foreground mt-1" />
          <div>
            <span className="text-sm text-muted-foreground">First Synced</span>
            <p className="text-sm font-medium">{formatDateTime(firstSyncedAt)}</p>
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(firstSyncedAt)}
            </p>
          </div>
        </div>

        {/* Last Synced */}
        {lastSyncedAt && (
          <div className="flex items-start gap-3">
            <RefreshCw className="h-4 w-4 text-muted-foreground mt-1" />
            <div>
              <span className="text-sm text-muted-foreground">Last Synced</span>
              <p className="text-sm font-medium">{formatDateTime(lastSyncedAt)}</p>
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(lastSyncedAt)}
              </p>
            </div>
          </div>
        )}

        {/* Record Timestamps */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Record Created</span>
            <span>{formatDateTime(createdAt)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Record Updated</span>
            <span>{formatDateTime(updatedAt)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
